import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import { Plus, Calendar, Mail, Phone, MessageSquare, Pencil, Trash2, AlertCircle, Loader2, ChevronDown } from 'lucide-preact';
import { TouchPointType, EmailEventType, EmailOffsetUnit } from '@/types/touchPoint';
import { formatEmailRule } from '@/utils/emailRuleFormatter';
import { TouchPointModal } from '../modals/TouchPointModal';
import { EmailActivationPanel } from '../modals/EmailActivationPanel';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { useLookups } from '@/hooks/useLookups';
import { useTouchPointManager } from '@/hooks/useTouchPointManager';
import { buildTouchPointCreateDto, buildTouchPointUpdateDto } from '@/utils/touchPointDtos';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
const FILTER_OPTIONS = [
    { key: 'All', label: 'All' },
    { key: 'Email', label: 'Email' },
    { key: 'Phone', label: 'Phone' },
    { key: 'Social', label: 'Social' },
];
// Sort key for touchpoints in the flat list. Returns ms-since-epoch when
// a knowable fire time exists; otherwise pushes to the end. For Email
// Kind=Event, the anchor's absolute time is derived from the parent Web
// when the anchor is event-relative (OnlineEventStart / OnlineEventEnd).
// Per-recipient anchors (WebRegistered, AssetDownloaded, etc.) have no
// global time and sort to the end.
function sortableDate(tp, web) {
    if (tp.EmailProperties?.Rule) {
        const rule = tp.EmailProperties.Rule;
        if (rule.Kind === 'Scheduled' && rule.SendAtAbsolute) {
            return new Date(rule.SendAtAbsolute).getTime();
        }
        if (rule.Kind === 'Event' && rule.EventType && rule.OffsetUnit && rule.OffsetValue != null) {
            const anchorMs = anchorTimeMs(rule.EventType, web);
            if (anchorMs !== null) {
                return anchorMs + offsetToMs(rule.OffsetValue, rule.OffsetUnit);
            }
        }
        return Number.MAX_SAFE_INTEGER;
    }
    const raw = tp.PhoneProperties?.ScheduledDate || tp.SocialProperties?.ScheduledDate || '';
    const t = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
}
function anchorTimeMs(eventType, web) {
    switch (eventType) {
        case EmailEventType.OnlineEventStart:
            return web.OnlineEvent?.StartDateTime ? new Date(web.OnlineEvent.StartDateTime).getTime() : null;
        case EmailEventType.OnlineEventEnd:
            return web.OnlineEvent?.EndDateTime ? new Date(web.OnlineEvent.EndDateTime).getTime() : null;
        case EmailEventType.WebRegistered:
        case EmailEventType.AssetDownloaded:
        case EmailEventType.MeetingBooked:
        case EmailEventType.DemoBooked:
            // Per-recipient anchors have no global time — caller sorts these to the end.
            return null;
        default:
            return assertNever(eventType);
    }
}
function offsetToMs(value, unit) {
    switch (unit) {
        case EmailOffsetUnit.Minute: return value * 60000;
        case EmailOffsetUnit.Hour: return value * 3600000;
        case EmailOffsetUnit.Day: return value * 86400000;
        default: return assertNever(unit);
    }
}
function assertNever(x) {
    throw new Error(`Unhandled EmailSendRule enum value: ${String(x)}`);
}
export function WebActivationsTab({ clientCompanyId, web, workspaceId, campaignId }) {
    const { updateContext } = useAIChatContextUpdater();
    // Use the shared touchpoint manager hook
    const { touchPoints, loading, error, saving, deleting, fetchTouchPoints, createTouchPoint, updateTouchPoint, deleteTouchPoint, } = useTouchPointManager({
        workspaceId,
        campaignId,
        webId: web.Id,
        typeFilters: ['Email', 'Phone', 'Social'],
        isCampaignLevel: false,
    });
    // Modal states (Phone / Social touchpoints stay in TouchPointModal)
    const [showModal, setShowModal] = useState(false);
    const [modalFixedType, setModalFixedType] = useState(undefined);
    const [editingTouchPoint, setEditingTouchPoint] = useState(null);
    // Email panel state (Email touchpoints use the slide-over editor)
    const [showEmailPanel, setShowEmailPanel] = useState(false);
    const [emailPanelTouchPoint, setEmailPanelTouchPoint] = useState(null);
    // Type-chooser menu for the New Activation button
    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const createMenuRef = useRef(null);
    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingTouchPoint, setDeletingTouchPoint] = useState(null);
    // Activation filter chip strip (above the flat list)
    const [activeFilter, setActiveFilter] = useState('All');
    // Precomputed counts for the filter chips
    const typeCounts = useMemo(() => {
        const counts = { All: touchPoints.length, Email: 0, Phone: 0, Social: 0 };
        for (const tp of touchPoints) {
            if (tp.Type === 'Email')
                counts.Email++;
            else if (tp.Type === 'Phone')
                counts.Phone++;
            else if (tp.Type === 'Social')
                counts.Social++;
        }
        return counts;
    }, [touchPoints]);
    // Flat list, filtered + sorted chronologically using web-aware anchor times
    const filteredSorted = useMemo(() => {
        const subset = activeFilter === 'All'
            ? touchPoints
            : touchPoints.filter(tp => tp.Type === activeFilter);
        return [...subset].sort((a, b) => sortableDate(a, web) - sortableDate(b, web));
    }, [touchPoints, activeFilter, web]);
    // Asset ids that other email touchpoints on this Web have bound. When editing
    // an existing touchpoint, exclude that touchpoint's own asset so the picker
    // still shows it in the "Unused" group (rather than the user's own pick
    // appearing as "Already used").
    const usedAssetIds = useMemo(() => {
        const ids = new Set();
        for (const tp of touchPoints) {
            if (tp.Type !== 'Email')
                continue;
            const aid = tp.EmailProperties?.EmailAssetId;
            if (!aid)
                continue;
            if (emailPanelTouchPoint && tp.Id === emailPanelTouchPoint.Id)
                continue;
            ids.add(aid);
        }
        return ids;
    }, [touchPoints, emailPanelTouchPoint]);
    const lookups = useLookups();
    // Update AI context when viewing activations
    useEffect(() => {
        updateContext({
            workspaceId,
            campaignId,
            webId: web.Id,
            location: ChatLocations.TouchPointEditor,
            entityType: ChatEntityTypes.Web,
            entityId: web.Id,
            viewMode: ChatViewModes.View,
            availableFields: [],
            availableActions: ['Create Activation', 'View Activations'],
        });
    }, [workspaceId, campaignId, web.Id, updateContext]);
    // Touchpoint types passed to the legacy modal — Meeting lives in its own tab,
    // Email now has its own slide-over panel.
    const touchPointTypes = lookups.getTouchPointTypes()
        .filter(x => x.Key !== 'Meeting' && x.Key !== 'Email')
        .map(x => ({ Id: x.Key, DisplayName: x.Description }));
    const handleCreateEmail = () => {
        setCreateMenuOpen(false);
        setEmailPanelTouchPoint(null);
        setShowEmailPanel(true);
    };
    const handleCreateOther = (type) => {
        setCreateMenuOpen(false);
        setEditingTouchPoint(null);
        setModalFixedType(type);
        setShowModal(true);
    };
    const handleEdit = (touchPoint) => {
        if (touchPoint.Type === TouchPointType.Email) {
            setEmailPanelTouchPoint(touchPoint);
            setShowEmailPanel(true);
            return;
        }
        setEditingTouchPoint(touchPoint);
        setModalFixedType(touchPoint.Type);
        setShowModal(true);
    };
    // Close create-menu on outside click
    useEffect(() => {
        if (!createMenuOpen)
            return;
        const onClick = (e) => {
            if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
                setCreateMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [createMenuOpen]);
    const handleCloseModal = () => {
        if (saving)
            return;
        setShowModal(false);
        setEditingTouchPoint(null);
        // Reset AI context back to View mode
        updateContext({
            workspaceId,
            campaignId,
            webId: web.Id,
            location: ChatLocations.TouchPointEditor,
            entityType: ChatEntityTypes.Web,
            entityId: web.Id,
            viewMode: ChatViewModes.View,
            availableFields: [],
            availableActions: ['Create Activation', 'View Activations'],
        });
    };
    const handleSave = async (formData) => {
        if (editingTouchPoint) {
            // Update existing touchpoint
            const updateDto = buildTouchPointUpdateDto(formData, editingTouchPoint.ETag);
            const result = await updateTouchPoint(editingTouchPoint.Id, updateDto, 'Activation Updated');
            if (result.success) {
                setShowModal(false);
                setEditingTouchPoint(null);
            }
        }
        else {
            // Create new touchpoint
            const createDto = buildTouchPointCreateDto(formData, campaignId, web.Id);
            const result = await createTouchPoint(createDto, 'Activation Created');
            if (result.success) {
                setShowModal(false);
                setEditingTouchPoint(null);
            }
        }
    };
    const handleDeleteClick = (touchPoint) => {
        setDeletingTouchPoint(touchPoint);
        setShowDeleteConfirm(true);
    };
    const handleDeleteConfirm = async () => {
        if (!deletingTouchPoint)
            return;
        const result = await deleteTouchPoint(deletingTouchPoint.Id, deletingTouchPoint.Name, 'Activation Deleted');
        if (result.success) {
            setShowDeleteConfirm(false);
            setDeletingTouchPoint(null);
        }
    };
    const handleDeleteCancel = () => {
        if (!deleting) {
            setShowDeleteConfirm(false);
            setDeletingTouchPoint(null);
        }
    };
    const formatDateTime = (isoString) => {
        try {
            const date = new Date(isoString);
            // Use UTC methods since we store dates at UTC midnight
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
            });
        }
        catch (e) {
            return isoString;
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("h2", { className: "text-xl font-semibold text-slate-900 dark:text-white", children: "Activations" }), _jsxs("div", { className: "relative", ref: createMenuRef, children: [_jsxs("button", { onClick: () => setCreateMenuOpen(v => !v), disabled: loading, "aria-haspopup": "menu", "aria-expanded": createMenuOpen, className: "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors", children: [_jsx(Plus, { className: "w-3.5 h-3.5" }), _jsx("span", { children: "New Activation" }), _jsx(ChevronDown, { className: "w-3.5 h-3.5 opacity-80" })] }), createMenuOpen && (_jsxs("div", { role: "menu", className: "absolute right-0 mt-1 w-52 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-20 py-1", children: [_jsx(CreateMenuItem, { onClick: handleCreateEmail, icon: _jsx(Mail, { className: "w-4 h-4" }), title: "Email activation", subtitle: "Asset + send rule" }), _jsx(CreateMenuItem, { onClick: () => handleCreateOther(TouchPointType.Phone), icon: _jsx(Phone, { className: "w-4 h-4" }), title: "Phone touchpoint", subtitle: "Scheduled call" }), _jsx(CreateMenuItem, { onClick: () => handleCreateOther(TouchPointType.Social), icon: _jsx(MessageSquare, { className: "w-4 h-4" }), title: "Social touchpoint", subtitle: "Scheduled post" })] }))] })] }), loading && (_jsx("div", { className: "flex items-center justify-center py-12", children: _jsx(Loader2, { className: "w-6 h-6 text-primary-600 animate-spin" }) })), error && !loading && (_jsx("div", { className: "rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4", children: _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(AlertCircle, { className: "w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" }), _jsxs("div", { children: [_jsx("h3", { className: "text-sm font-medium text-red-800 dark:text-red-300", children: "Failed to load activations" }), _jsx("p", { className: "text-sm text-red-700 dark:text-red-400 mt-1", children: error }), _jsx("button", { onClick: fetchTouchPoints, className: "mt-2 text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline", children: "Try again" })] })] }) })), !loading && !error && touchPoints.length === 0 && (_jsxs("div", { className: "text-center py-12", children: [_jsx(Calendar, { className: "w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-3" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-400", children: "No activations yet. Create your first touchpoint to get started." })] })), !loading && !error && touchPoints.length > 0 && (_jsx("div", { className: "flex items-center gap-1 mb-3 text-sm", children: FILTER_OPTIONS.map(f => (_jsxs("button", { type: "button", onClick: () => setActiveFilter(f.key), "aria-pressed": activeFilter === f.key, className: 'px-2.5 py-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ' +
                                (activeFilter === f.key
                                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'), children: [_jsx("span", { children: f.label }), _jsx("span", { className: "ml-1.5 text-xs text-slate-400 dark:text-slate-500", children: typeCounts[f.key] })] }, f.key))) })), !loading && !error && touchPoints.length > 0 && (_jsx("div", { className: "-mx-3 divide-y divide-slate-100 dark:divide-slate-800", children: filteredSorted.length === 0 ? (_jsxs("div", { className: "px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400", children: ["No ", activeFilter.toLowerCase(), " activations yet."] })) : (filteredSorted.map(tp => (_jsx(ActivationRow, { tp: tp, formatDateTime: formatDateTime, onEdit: () => handleEdit(tp), onDelete: () => handleDeleteClick(tp) }, tp.Id)))) }))] }), showModal && (_jsx(TouchPointModal, { touchPoint: editingTouchPoint, touchPointTypes: touchPointTypes, fixedType: modalFixedType, onClose: handleCloseModal, onSave: handleSave, saving: saving, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, webId: web.Id })), _jsx(EmailActivationPanel, { isOpen: showEmailPanel, touchPoint: emailPanelTouchPoint, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, webId: web.Id, usedAssetIds: usedAssetIds, onClose: () => {
                    setShowEmailPanel(false);
                    setEmailPanelTouchPoint(null);
                }, onSaved: () => {
                    fetchTouchPoints();
                } }), _jsx(ConfirmationModal, { isOpen: showDeleteConfirm, title: "Delete Activation", message: `Are you sure you want to delete "${deletingTouchPoint?.Name}"? This action cannot be undone.`, type: "danger", confirmText: "Delete", onConfirm: handleDeleteConfirm, onCancel: handleDeleteCancel, loading: deleting })] }));
}
function ActivationRow({ tp, formatDateTime, onEdit, onDelete }) {
    const primary = primarySchedule(tp, formatDateTime);
    const secondary = secondaryDetail(tp);
    return (_jsxs("div", { role: "button", tabIndex: 0, onClick: onEdit, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEdit();
            }
        }, className: "group flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset", children: [_jsx(TypeGlyph, { type: tp.Type }), _jsxs("div", { className: "flex-1 min-w-0 flex items-baseline gap-3 flex-wrap", children: [_jsx("span", { className: "text-sm font-medium text-slate-900 dark:text-slate-100 truncate", children: tp.Name }), _jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400 truncate", children: primary })] }), secondary && (_jsx("span", { className: "hidden sm:inline text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap", children: secondary })), _jsxs("div", { className: "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 flex items-center gap-1 transition-opacity flex-shrink-0", children: [_jsx("button", { type: "button", onClick: (e) => { e.stopPropagation(); onEdit(); }, className: "p-1.5 text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500", "aria-label": "Edit activation", title: "Edit", children: _jsx(Pencil, { className: "w-3.5 h-3.5" }) }), _jsx("button", { type: "button", onClick: (e) => { e.stopPropagation(); onDelete(); }, className: "p-1.5 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500", "aria-label": "Delete activation", title: "Delete", children: _jsx(Trash2, { className: "w-3.5 h-3.5" }) })] })] }));
}
function TypeGlyph({ type }) {
    const Icon = type === 'Email' ? Mail : type === 'Phone' ? Phone : type === 'Social' ? MessageSquare : Calendar;
    return (_jsx("div", { className: "w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center flex-shrink-0", "aria-label": type, children: _jsx(Icon, { className: "w-3.5 h-3.5" }) }));
}
function primarySchedule(tp, formatDateTime) {
    if (tp.Type === 'Email' && tp.EmailProperties?.Rule) {
        return formatEmailRule(tp.EmailProperties.Rule);
    }
    if (tp.Type === 'Phone' && tp.PhoneProperties?.ScheduledDate) {
        return formatDateTime(tp.PhoneProperties.ScheduledDate);
    }
    if (tp.Type === 'Social' && tp.SocialProperties?.ScheduledDate) {
        return formatDateTime(tp.SocialProperties.ScheduledDate);
    }
    return '—';
}
function secondaryDetail(tp) {
    if (tp.Type === 'Phone' && tp.PhoneProperties) {
        const n = tp.PhoneProperties.NumberOfDials;
        return `${n} ${n === 1 ? 'dial' : 'dials'}`;
    }
    if (tp.Type === 'Social' && tp.SocialProperties?.Platform) {
        return tp.SocialProperties.Platform;
    }
    return null;
}
function CreateMenuItem({ icon, title, subtitle, onClick }) {
    return (_jsxs("button", { type: "button", role: "menuitem", onClick: onClick, className: "w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 focus:bg-slate-50 dark:focus:bg-slate-700 focus:outline-none transition-colors", children: [_jsx("span", { className: "mt-0.5 text-slate-500 dark:text-slate-400", children: icon }), _jsxs("span", { className: "min-w-0", children: [_jsx("span", { className: "block text-sm font-medium text-slate-900 dark:text-slate-100", children: title }), _jsx("span", { className: "block text-xs text-slate-500 dark:text-slate-400", children: subtitle })] })] }));
}
